// ============================================
// SERVICE: MEDICAMENTOS
// ============================================
// Funções para gerenciar medicamentos via API
// ============================================

import { API_CONFIG } from './staticDataConfig';

const baseURL = API_CONFIG.BASE_URL;

export interface Medicamento {
  id: number;
  nome_medicamento: string;
  descricao?: string;
  categoria?: string;
  ativo: boolean;
  ordem: number;
}

class MedicamentosService {
  private getToken(): string | null {
    return sessionStorage.getItem('token');
  }

  private getHeaders(): HeadersInit {
    const token = this.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // Buscar todos os medicamentos (ativos ou incluindo inativos)
  async obterTodos(incluirInativos: boolean = false): Promise<Medicamento[]> {
    try {
      const url = incluirInativos 
        ? `${baseURL}/medicamentos?incluirInativos=true`
        : `${baseURL}/medicamentos`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`Erro ao buscar medicamentos: ${response.statusText}`);
      }

      const data = await response.json();
      // console.log(`[MedicamentosService] Medicamentos obtidos: ${data.medicamentos?.length || 0} (incluirInativos: ${incluirInativos})`);
      return data.medicamentos || [];
    } catch (error) {
      console.error('[MedicamentosService] Erro ao obter medicamentos:', error);
      throw error;
    }
  }

  // Adicionar novo medicamento
  async adicionar(medicamento: {
    nome_medicamento: string;
    descricao?: string;
    categoria?: string;
    ordem?: number;
  }): Promise<Medicamento> {
    try {
      const response = await fetch(`${baseURL}/medicamentos`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(medicamento)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.mensagem || 'Erro ao adicionar medicamento');
      }

      const data = await response.json();
      return data.medicamento;
    } catch (error) {
      console.error('[MedicamentosService] Erro ao adicionar medicamento:', error);
      throw error;
    }
  }

  // Atualizar medicamento existente
  async atualizar(id: number, medicamento: {
    nome_medicamento?: string;
    descricao?: string;
    categoria?: string;
    ordem?: number;
    ativo?: boolean;
  }): Promise<Medicamento> {
    try {
      const response = await fetch(`${baseURL}/medicamentos/${id}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(medicamento)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.mensagem || 'Erro ao atualizar medicamento');
      }

      const data = await response.json();
      return data.medicamento;
    } catch (error) {
      console.error('[MedicamentosService] Erro ao atualizar medicamento:', error);
      throw error;
    }
  }

  // Excluir medicamento (proteção inteligente)
  async excluir(id: number, forcarDelete: boolean = false): Promise<any> {
    try {
      const url = forcarDelete 
        ? `${baseURL}/medicamentos/${id}?forcarDelete=true`
        : `${baseURL}/medicamentos/${id}`;
        
      const response = await fetch(url, {
        method: 'DELETE',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.mensagem || 'Erro ao excluir medicamento');
      }
      
      return await response.json();
    } catch (error) {
      console.error('[MedicamentosService] Erro ao excluir medicamento:', error);
      throw error;
    }
  }
}

export const medicamentosService = new MedicamentosService();
